/* eslint-disable max-lines -- lots of lines are due to imports */
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatError,
  MatFormField,
  MatFormFieldModule,
  MatLabel,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { assert, isDefined } from '@sama/js-core';
import { pick } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UuidService } from '../../../../cross-team-shared/misc-services/uuid.service';
import { assertDefined } from '../../../../cross-team-shared/misc-utils/strict-record';
import { ScrollEnablerDirective } from '../../../../cross-team-shared/scroll-enabler.directive';
import { PointType } from '../../../../team-apis/otterdam/answers/shape-typings';
import { ShapeFeedback } from '../../../../team-apis/otterdam/feedback';
import {
  getOutputById,
  getOutputByName,
  isAnnotationOutput,
} from '../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { getAnnotationOutput } from '../../../h3ml/state/task-workspace-store-utils';
import { RootFocusedShapeService } from '../../../outputs/annotation/annotation-shared/utils/root-focused-shape.service';
import { SamplingWorkspaceStore } from '../../../sampling-workspace/state/sampling-workspace-store';
import { ShapeService } from '../../misc-services/shape.service';
import { ShapeFeedbackCrudService } from '../crud/shape-feedback-crud.service';
import { FeedbackListService } from '../feedback-list.service';
import { FeedbackPaneComponent } from '../feedback-pane/feedback-pane.component';
import { ShapeFeedbackDialogService } from '../shape-feedback-dialog.service';

@Component({
  selector: 'h3-add-edit-feedback-dialog',
  templateUrl: './add-edit-feedback-dialog.component.html',
  styleUrls: ['./add-edit-feedback-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FeedbackPaneComponent,
    AsyncPipe,
    MatFormField,
    ReactiveFormsModule,
    MatSelect,
    NgForOf,
    NgIf,
    ScrollEnablerDirective,
    MatOption,
    MatCheckbox,
    MatError,
    MatButton,
    MatInput,
    MatLabel,
    MatFormFieldModule,
  ],
})
export class AddEditFeedbackDialogComponent
  extends DirectiveSuperclass
  implements OnInit
{
  shapeId$ = new BehaviorSubject<number | undefined>(undefined);
  title$ = new BehaviorSubject('');
  selectedOutputName$ = this.taskWorkspaceStore('selectedOutputName').$;
  maxFrameNumber$ = new BehaviorSubject(0);
  shouldDisplayFrameInputs$ = new BehaviorSubject(true);
  form!: FormGroup;
  displayedErrorTags$!: Observable<string[]>;

  private feedbackId$ = new BehaviorSubject<undefined | string>(undefined);
  private uuidService = inject(UuidService);
  private feedbackCoordinate?: PointType;
  private focusedShapeService = inject(RootFocusedShapeService);
  private target$ = this.taskWorkspaceStore('feedbackPopover')('target').$;
  private shapeService = inject(ShapeService);
  private initialFocusedOutputId =
    this.taskWorkspaceStore.getSelectedOutput().id;

  constructor(
    private formBuilder: FormBuilder,
    private shapeFeedbackCrudService: ShapeFeedbackCrudService,
    private shapeFeedbackDialogService: ShapeFeedbackDialogService,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private feedbackListService: FeedbackListService,
    private samplingWorkspaceStore: SamplingWorkspaceStore,
    injector: Injector,
  ) {
    super(injector);
    this.setUpForm(this.maxFrameNumber$.value);
    this.shouldDisplayFrameInputs$.next(this.maxFrameNumber$.value > 1);
  }

  ngOnInit() {
    this.subscribeTo(this.selectedOutputName$, (outputName) => {
      const maxFrameNumber =
        this.taskWorkspaceStore.getMaxFrameNumber(outputName);
      if (this.maxFrameNumber$.value !== maxFrameNumber) {
        this.maxFrameNumber$.next(maxFrameNumber);
        this.shouldDisplayFrameInputs$.next(maxFrameNumber > 1);
        this.setUpForm(maxFrameNumber);
      }
    });
    this.displayedErrorTags$ = this.getShapeFeedbackTags$();
    this.setUpSubscriptionToAllFramesCheckbox();

    this.subscribeTo(
      merge(this.feedbackListService.getSelectedId$(), this.target$),
      this.initDialog,
    );
  }

  closeDialog() {
    this.shapeFeedbackDialogService.closeDialog();
  }

  saveFeedback() {
    this.form.disable();

    const feedbackId = this.feedbackId$.value ?? this.uuidService.getUuidV4();
    const outputId = this.getOutputIdForFeedback();
    this.shapeFeedbackCrudService.createUpdateFeedback(
      outputId,
      feedbackId,
      this.buildShapeFeedbackAttributes(),
    );
    this.shapeFeedbackDialogService.closeDialog(feedbackId);
    this.feedbackCoordinate = undefined;
  }

  private initDialog() {
    const feedback = this.shapeFeedbackDialogService.getSelectedFeedback();
    const focusedShape = this.focusedShapeService.getFocusedShapeStore();
    if (isDefined(focusedShape)) {
      this.shapeId$.next(focusedShape.state().index);
    }
    this.initFormValue();
    this.feedbackId$.next(feedback?.feedbackId);
    if (isDefined(feedback)) {
      this.setUpExistingFeedback(feedback);
    }

    if (this.isForMissingShape()) {
      this.form.controls.errorTag.disable();
      this.feedbackCoordinate = feedback
        ? feedback.coordinates
        : this.taskWorkspaceStore.state().feedbackPopover.missingShapePoint;
    } else {
      this.form.controls.errorTag.enable();
    }
    this.title$.next(this.getTitle());
  }

  private setUpForm(maxFrameNumber: number) {
    this.form = this.formBuilder.group(
      {
        errorTag: [null as string | null, [Validators.required]],
        startFrame: [0, this.getFrameValidators(maxFrameNumber)],
        endFrame: [0, this.getFrameValidators(maxFrameNumber)],
        allFrames: false,
        comment: '',
      },
      {
        validators: (formGroup) => {
          if (maxFrameNumber <= 1) {
            return null;
          }
          const { startFrame, endFrame } = formGroup.getRawValue();
          return startFrame > endFrame ? { invalidFrameNumber: true } : null;
        },
      },
    );
  }

  private getShapeFeedbackTags$() {
    const output = getAnnotationOutput(
      this.taskWorkspaceStore,
      this.taskWorkspaceStore.state().selectedOutputName,
    );
    const errorTags = (output.meta.custom_shape_level_error_tags || []).concat(
      output.mandatory_shape_level_error_tags,
    );
    return this.shapeId$.pipe(
      map((shapeId) =>
        shapeId
          ? errorTags.filter((tag) => tag !== 'Missing Shape')
          : errorTags,
      ),
    );
  }

  private setUpExistingFeedback(feedback: ShapeFeedback) {
    this.shapeId$.next(feedback.shapeId);
    const project = this.taskWorkspaceStore.state().project;
    const output = getOutputById(project, feedback.outputId);
    assert(
      isAnnotationOutput(output),
      `Annotation output not found for feedback: ${feedback.feedbackId}`,
    );
    this.form.patchValue(
      pick(feedback, 'errorTag', 'startFrame', 'endFrame', 'comment'),
    );
  }

  private setUpSubscriptionToAllFramesCheckbox() {
    this.subscribeTo(this.form.controls.allFrames.valueChanges, (isChecked) => {
      if (isChecked) {
        this.form.controls.startFrame.disable();
        this.form.controls.endFrame.disable();

        if (this.shapeId$.value) {
          this.setFrameRangeFromVisibleFrames();
        } else {
          this.form.controls.startFrame.setValue(0);
          this.form.controls.endFrame.setValue(this.maxFrameNumber$.value);
        }
      } else {
        this.form.controls.startFrame.enable();
        this.form.controls.endFrame.enable();
      }
    });
  }

  private setFrameRangeFromVisibleFrames() {
    const shape = this.focusedShapeService.getFocusedShapeStore()!.state();
    const visibleFrames = shape.key_locations
      .filter((location) => location.visibility !== 0)
      .map((location) => location.frame_number);
    const lastVisibleKeyLocation = Math.max(...visibleFrames);
    const invisibleFrames = shape.key_locations
      .filter(
        (location) =>
          location.visibility === 0 &&
          location.frame_number > lastVisibleKeyLocation,
      )
      .map((location) => location.frame_number);

    const lastVisibleFrame = invisibleFrames.length
      ? Math.min(...invisibleFrames) - 1
      : this.maxFrameNumber$.value;

    this.form.controls.startFrame.setValue(Math.min(...visibleFrames));
    this.form.controls.endFrame.setValue(lastVisibleFrame);
  }

  private isForMissingShape() {
    return !this.shapeId$.value;
  }

  private getFrameValidators(max: number) {
    const validators = [Validators.min(0), Validators.max(max)];
    if (max > 1) {
      validators.push(Validators.required);
    }
    return validators;
  }

  private initFormValue() {
    const { selectedFrameRange } =
      this.taskWorkspaceStore.state().feedbackPopover;
    this.form.patchValue({
      errorTag: this.isForMissingShape() ? 'Missing Shape' : null,
      startFrame: selectedFrameRange[0],
      endFrame: selectedFrameRange[1],
      allFrames: false,
      comment: '',
    });
    this.form.markAsUntouched();
  }

  private getTitle() {
    const shapeId = this.shapeId$.value;
    const feedbackId = this.feedbackId$.value;
    const titleForShape = shapeId ? '#' + shapeId : '';
    return feedbackId
      ? 'Edit feedback ' + titleForShape
      : 'Add feedback ' + titleForShape;
  }

  private buildShapeFeedbackAttributes() {
    if (!this.isForMissingShape()) {
      this.feedbackCoordinate = undefined;
    }

    const formValues = this.form.getRawValue();
    return {
      sampleId: this.samplingWorkspaceStore.state().sample.id,
      stepId: this.taskWorkspaceStore.getLastSubmission().step_id,
      shapeId: this.shapeId$.value,
      errorTag: formValues.errorTag!, // errorTag is required
      comment: formValues.comment || '',
      // startFrame is required
      startFrame:
        this.maxFrameNumber$.value > 1 ? formValues.startFrame! : undefined,
      // endFrame is required
      endFrame:
        this.maxFrameNumber$.value > 1 ? formValues.endFrame! : undefined,
      coordinates: this.feedbackCoordinate,
    };
  }

  private getOutputIdForFeedback() {
    if (this.feedbackId$.value) {
      const selectedFeedback =
        this.shapeFeedbackDialogService.getSelectedFeedback();
      assertDefined(selectedFeedback);
      return selectedFeedback.outputId;
    }

    if (this.shapeId$.value) {
      const shapeStore = this.shapeService.getShapeStore(this.shapeId$.value);
      assert(shapeStore, 'There is no focused shape');
      const outputName = shapeStore.state().outputName;
      assertDefined(outputName);
      return getOutputByName(
        this.taskWorkspaceStore.state().project,
        outputName,
      ).id;
    }

    // new missing shape marker feedback is being added, so no shapeId
    return this.initialFocusedOutputId;
  }
}
